import type { Vec3 } from './assemblyPose';
import { faceBasis, faceNormal, type FaceWord } from './partFaces';
import { isLeftRight, type PartItem } from './partsRun';
import { keptHand, productMiddleX } from './holeLines';
import type { HolesContext } from './stepHolesPrompt';
import type { MeshOf } from './stepHolesRun';
import { loadDeps, makeLighting, releaseContext } from './meshScene';
import { tallyRenderer } from './gpuTally';
import { loadPlacedMesh } from './partSolids';
import { namedIn, shownId } from './partsCatalogue';
import { runLabel, slotRuns, type HoleSlot } from './holeSlots';
import { holeOrigins, type HoleAt, type StepHole } from './stepHoles';
import { swapFrom } from './holeLines';
import type { Job3D } from './threeDApi';

// WHAT FIND HOLES IS SHOWN OF THE PARTS, PER GROUP OF HOLES — two pictures, from the whole product
// as it stands, where it used to be six renders of each part on its own from every side.
//
// The call is asked one thing: where, on one face, a piece of hardware goes in (see holeSlots). So
// it is shown that face, straight on and close: the part it enters see-through, where the part the
// hardware goes on into touches it outlined, the places in that outline where a hole works shaded
// (see holeArea), and a ruler in millimetres
// along two edges, counted from the edges the answer counts from — a figure read off a scale
// rather than a distance guessed off pixels of a 482 mm frame. And the joint assembled, from three
// quarters, so the close view has somewhere to be.
//
// THE PARTS STAND WHERE THE PRODUCT BUILT THEM (see loadPlacedMesh), the other hand of a pair
// reflected across the product's middle, as everywhere else.

const PX = 640;
// The ruler's strip under and beside the picture, in pixels.
const STRIP = 72;
// One line of the key above the picture, in pixels.
const KEY_LINE = 28;
const PATCH_INK = '#dc2626';
const DRILLED_INK = '#1d4ed8';
const AREA_INK = 'rgb(22, 163, 74)';
const AREA_ALPHA = 0.55;

type Shot = { url: string; caption: string; group: string };

// A NICE STEP for a ruler across `span` millimetres: about eight ticks.
const stepFor = (span: number) => [1, 2, 5, 10, 20, 50, 100, 200].find((s) => span / s <= 10) ?? 500;

export async function jointShots(
  slots: HoleSlot[],
  items: PartItem[],
  meshOf: (id: string) => Job3D | null,
  mirrorX: number | null,
  undrilled = false,
  // THE HOLES THE ANSWER MADE, once it is in: each run's crossed on its face where it was drilled.
  // Only the badge's picture of a run that answered — never one a call is sent.
  drilled: StepHole[] = [],
): Promise<{ shown: string[]; views: Shot[] }> {
  const shown: string[] = [];
  const views: Shot[] = [];
  for (const run of slotRuns(slots)) {
    try {
      const made = await shootRun(run[0], items, meshOf, mirrorX, undrilled,
        drilled.filter((h) => run.some((sl) => sl.id === h.id)));
      if (!made) continue;
      const label = runLabel(run);
      const group = `${label} — ${run[0].face} of ${shownId(run[0].part)} ${namedIn(items[Number(run[0].part)], 'en')}`;
      // AN UNDRILLED PICTURE THAT COULD NOT BE: a part changed after its bores is shown as it is, said.
      const as = made.drilled ? ' — drilled: changed since, no undrilled state kept' : '';
      const crossed = made.crossed ? ' — where they were drilled' : '';
      views.push({ url: made.whole, caption: `assembled${as}`, group }, { url: made.face, caption: `face, straight on${crossed}${as}`, group });
      shown.push(label);
    } catch {
      // Not pictured, and the prompt lists only the groups that are.
    }
  }
  return { shown, views };
}

async function shootRun(
  slot: HoleSlot,
  items: PartItem[],
  meshOf: (id: string) => Job3D | null,
  mirrorX: number | null,
  undrilled: boolean,
  holes: StepHole[],
): Promise<{ face: string; whole: string; drilled: boolean; crossed: boolean } | null> {
  const { THREE, RoomEnvironment } = await loadDeps();
  // A PART AS ITS UNIT FOR THE SLOT'S HAND: the kept file, or that file reflected.
  let drilled = false;
  const placed = async (part: string) => {
    const job = meshOf(part);
    if (!job) return null;
    const got = await loadPlacedMesh(job, items[Number(part)]?.size, undrilled);
    if (!got) return null;
    drilled ||= got.drilled;
    const holder = new THREE.Group();
    holder.add(got.root);
    const box = new THREE.Box3().setFromObject(got.root);
    const kept = mirrorX != null
      ? keptHand({ min: box.min.toArray() as Vec3, max: box.max.toArray() as Vec3 }, mirrorX) : null;
    if (slot.side && isLeftRight(items[Number(part)]) && mirrorX != null && kept !== slot.side) {
      holder.scale.x = -1;
      holder.position.x = 2 * mirrorX;
    }
    holder.updateMatrixWorld(true);
    return holder;
  };
  const entry = await placed(slot.part);
  if (!entry) return null;
  const next = slot.next ? await placed(slot.next) : null;

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: true });
  tallyRenderer(renderer);
  renderer.setPixelRatio(1);
  renderer.setSize(PX, PX, false);
  renderer.setClearColor(0xffffff, 1);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  const scene = new THREE.Scene();
  const lighting = makeLighting(THREE, renderer, scene, RoomEnvironment);
  lighting.apply('studio');
  scene.add(entry);
  if (next) scene.add(next);
  try {
    const box = new THREE.Box3().setFromObject(entry);
    const centre = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const { u, v } = faceBasis(slot.face);
    const n = faceNormal(slot.face);
    const k = (a: Vec3) => a.findIndex((c) => c !== 0);
    const span = (a: Vec3) => size.getComponent(k(a));
    // A FIGURE ON THE FACE BACK INTO THE WORLD, along one of its axes — the inverse of patchOn.
    const along = (a: Vec3, mm: number) => centre.getComponent(k(a)) + (mm - span(a) / 2) * a[k(a)];
    // WHAT IS FRAMED, in millimetres across the face: the patch and as much again round it (20 mm
    // at least), or the whole face where none was measured.
    const range = (a: Vec3, r: { lo: number; hi: number } | undefined) => {
      if (!r) return { lo: 0, hi: span(a) };
      const pad = Math.max(20, (r.hi - r.lo) / 2);
      return { lo: Math.max(0, r.lo - pad), hi: Math.min(span(a), r.hi + pad) };
    };
    const ru = range(u, slot.patch?.u);
    const rv = range(v, slot.patch?.v);
    const side = Math.max(ru.hi - ru.lo, rv.hi - rv.lo);
    const mid = new THREE.Vector3().copy(centre);
    mid.setComponent(k(u), along(u, (ru.lo + ru.hi) / 2));
    mid.setComponent(k(v), along(v, (rv.lo + rv.hi) / 2));

    // 1. THE JOINT ASSEMBLED, from three quarters, in the parts' own materials: both parts whole and
    // a margin round them, the distance worked out so they fit — it was framed on the rail and a
    // patch's width, and showed a piece of leg.
    const all = new THREE.Box3().setFromObject(entry);
    if (next) all.union(new THREE.Box3().setFromObject(next));
    const sphere = all.getBoundingSphere(new THREE.Sphere());
    const persp = new THREE.PerspectiveCamera(30, 1, 1, 100000);
    const dir = new THREE.Vector3(...n).addScaledVector(new THREE.Vector3(...u), 0.7).add(new THREE.Vector3(0, 0, 0.6)).normalize();
    persp.position.copy(sphere.center).addScaledVector(dir, (sphere.radius / Math.sin((15 * Math.PI) / 180)) * 1.1);
    persp.up.set(0, 0, 1);
    persp.lookAt(sphere.center);
    renderer.render(scene, persp);
    const wholeUrl = renderer.domElement.toDataURL('image/png');

    // 2. THE FACE, STRAIGHT ON, in flat colours rather than wood: the entered part a faint grey with
    // its edges drawn. Its own texture at a third of its opacity was a mottled pink the rail could
    // hardly be told from. THE NEXT PART IS NOT DRAWN: it was solid blue behind, and filled the frame
    // round a rail under a seat — where the holes may go is the green area now.
    const paint = (root: import('three').Object3D, mat: import('three').Material, edges: boolean) => root.traverse((o) => {
      const mesh = o as import('three').Mesh;
      if (!mesh.isMesh) return;
      const old = mesh.material;
      if (Array.isArray(old)) old.forEach((x) => x.dispose());
      else old.dispose();
      mesh.material = mat;
      if (edges) {
        mesh.add(new THREE.LineSegments(
          new THREE.EdgesGeometry(mesh.geometry, 30),
          new THREE.LineBasicMaterial({ color: 0x334155 }),
        ));
      }
    });
    paint(entry, new THREE.MeshBasicMaterial({ color: 0xcbd5e1, transparent: true, opacity: 0.3, depthWrite: false }), true);
    if (next) scene.remove(next);
    const ortho = new THREE.OrthographicCamera(-side / 2, side / 2, side / 2, -side / 2, 0.1, 10000);
    const far = Math.max(size.x, size.y, size.z) + 1000;
    ortho.position.copy(mid).addScaledVector(new THREE.Vector3(...n), far);
    ortho.up.set(...v);
    ortho.lookAt(mid);
    ortho.updateMatrixWorld(true);
    renderer.render(scene, ortho);
    // WHICH PART IS WHICH, said on the picture: only the entry part is drawn, and seen through, its
    // two ends read as two parts.
    const named = (id: string) => `${shownId(id)} ${namedIn(items[Number(id)] ?? {}, 'en')}${slot.side && isLeftRight(items[Number(id)]) ? `, ${slot.side}` : ''}`;
    // EACH HOLE IN THE FACE'S OWN FIGURES, mm from the edges its rulers count from. A pair's holes are
    // kept in the left unit's words (see lineHoles), and the right one's face is said in its own.
    const [uFrom, vFrom] = holeOrigins(slot.face);
    const turn = slot.side === 'right' && isLeftRight(items[Number(slot.part)]);
    const fig = (at: HoleAt, from: FaceWord, a: Vec3) => {
      const said = turn ? swapFrom(at) : at;
      return 'centred' in said ? span(a) / 2 : said.from === from ? said.mm : span(a) - said.mm;
    };
    const crosses = holes.map((h) => ({ id: h.id, u: fig(h.u, uFrom, u), v: fig(h.v, vFrom, v) }));
    const faceUrl = withRuler(renderer.domElement, ortho, slot, { u, v, ru, rv, along, mid }, {
      entry: named(slot.part), next: slot.next ? named(slot.next) : null,
    }, crosses);
    return { face: faceUrl, whole: wholeUrl, drilled, crossed: crosses.length > 0 };
  } finally {
    for (const root of [entry, next]) {
      root?.traverse((o) => {
        const m = o as import('three').Mesh;
        m.geometry?.dispose?.();
        const mat = m.material;
        if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
        else (mat as import('three').Material | undefined)?.dispose?.();
      });
    }
    scene.environment = null;
    lighting.dispose();
    releaseContext(renderer);
  }
}

// THE FACE PICTURE WITH ITS RULERS: the render, the patch outlined, and ticks along the bottom and
// the left in millimetres from the edges the answer counts from — placed by projecting each tick's
// own point, so the scale is the picture's whatever way the camera came to be turned.
function withRuler(
  gl: HTMLCanvasElement,
  camera: import('three').Camera,
  slot: HoleSlot,
  f: {
    u: Vec3; v: Vec3; ru: { lo: number; hi: number }; rv: { lo: number; hi: number };
    along: (a: Vec3, mm: number) => number; mid: import('three').Vector3;
  },
  parts: { entry: string; next: string | null },
  crosses: { id: string; u: number; v: number }[],
): string {
  // THE KEY, a line per thing drawn, in a band above the picture: the grey is one part and the red
  // is where the other touches it — the other is not drawn (see shootRun).
  const keys: { swatch: 'part' | 'outline' | 'area' | 'cross'; text: string }[] = [
    { swatch: 'part', text: `${parts.entry}, seen through from its ${slot.face} face` },
    ...(slot.patch && parts.next ? [{ swatch: 'outline' as const, text: `where ${parts.next} touches it (not drawn)` }] : []),
    ...(slot.area?.ok.length ? [{ swatch: 'area' as const, text: 'where a hole works' }] : []),
    ...(crosses.length ? [{ swatch: 'cross' as const, text: 'where it was drilled, by the answer' }] : []),
  ];
  const band = 16 + keys.length * KEY_LINE;
  const out = document.createElement('canvas');
  out.width = PX + STRIP;
  out.height = band + PX + STRIP;
  const ctx = out.getContext('2d')!;
  // A CROSS, on a white halo so it reads over grey and green alike.
  const cross = (x: number, y: number, r: number) => {
    for (const [ink, width] of [['#ffffff', 6], [DRILLED_INK, 3]] as const) {
      ctx.strokeStyle = ink;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(x - r, y - r); ctx.lineTo(x + r, y + r);
      ctx.moveTo(x - r, y + r); ctx.lineTo(x + r, y - r);
      ctx.stroke();
    }
  };
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.font = '500 17px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'left';
  keys.forEach((key, i) => {
    const y = 12 + i * KEY_LINE;
    const x = STRIP;
    if (key.swatch === 'part') {
      ctx.fillStyle = '#e2e8f0';
      ctx.fillRect(x, y + 2, 28, 18);
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x, y + 2, 28, 18);
    } else if (key.swatch === 'outline') {
      ctx.strokeStyle = PATCH_INK;
      ctx.lineWidth = 3;
      ctx.setLineDash([7, 4]);
      ctx.strokeRect(x + 1.5, y + 3.5, 25, 15);
      ctx.setLineDash([]);
    } else if (key.swatch === 'cross') {
      cross(x + 14, y + 11, 8);
    } else {
      ctx.globalAlpha = AREA_ALPHA;
      ctx.fillStyle = AREA_INK;
      ctx.fillRect(x, y + 2, 28, 18);
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = '#0f172a';
    ctx.fillText(key.text, x + 40, y + 17);
  });
  // Everything below is the picture and its rulers, drawn as they were under the band.
  ctx.translate(0, band);
  ctx.drawImage(gl, STRIP, 0);
  const k = (a: Vec3) => a.findIndex((c) => c !== 0);
  // A point on the face, by its two figures, to a pixel of `out`.
  const px = (mmU: number, mmV: number) => {
    const p = f.mid.clone();
    p.setComponent(k(f.u), f.along(f.u, mmU));
    p.setComponent(k(f.v), f.along(f.v, mmV));
    p.project(camera);
    return { x: STRIP + ((p.x + 1) / 2) * PX, y: ((1 - p.y) / 2) * PX };
  };
  // WHERE A HOLE WORKS, cell by cell, under the outline — painted solid on a layer of its own and laid
  // down see-through once: the cells' middles are rounded to half a millimetre, so neighbours overlap,
  // and painted see-through one by one every overlap was a darker seam across the area.
  if (slot.area) {
    const layer = document.createElement('canvas');
    layer.width = out.width;
    layer.height = out.height;
    const lc = layer.getContext('2d')!;
    lc.fillStyle = AREA_INK;
    const { du, dv } = slot.area;
    for (const [cu, cv] of slot.area.ok) {
      const a = px(cu - du / 2, cv - dv / 2);
      const b = px(cu + du / 2, cv + dv / 2);
      lc.fillRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x) + 0.5, Math.abs(b.y - a.y) + 0.5);
    }
    ctx.globalAlpha = AREA_ALPHA;
    ctx.drawImage(layer, 0, 0);
    ctx.globalAlpha = 1;
  }
  if (slot.patch) {
    const a = px(slot.patch.u.lo, slot.patch.v.lo);
    const b = px(slot.patch.u.hi, slot.patch.v.hi);
    ctx.strokeStyle = PATCH_INK;
    ctx.lineWidth = 3;
    ctx.setLineDash([10, 6]);
    ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
    ctx.setLineDash([]);
  }
  ctx.fillStyle = '#0f172a';
  ctx.strokeStyle = '#0f172a';
  ctx.font = '600 20px ui-sans-serif, system-ui, sans-serif';
  ctx.lineWidth = 1.5;
  const [fromU, fromV] = holeOrigins(slot.face);
  // Words over the picture, on a white halo so they read over grey and green alike.
  const say = (text: string, x: number, y: number, align: CanvasTextAlign) => {
    ctx.textAlign = align;
    ctx.save();
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 5;
    ctx.strokeText(text, x, y);
    ctx.restore();
    ctx.fillText(text, x, y);
  };
  const tick = (x0: number, y0: number, x1: number, y1: number) => {
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  };
  // WHERE THE ANSWER DRILLED, each hole crossed and named beside it.
  for (const c of crosses) {
    const { x, y } = px(c.u, c.v);
    cross(x, y, 10);
    ctx.fillStyle = DRILLED_INK;
    say(c.id, x + 16, y - 10, 'left');
  }
  ctx.fillStyle = '#0f172a';
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1.5;

  if (slot.patch) {
    // ON THE OUTLINE ITSELF, from its corner: the figures the answer gives are counted from the
    // outline's own edges (see slotLines), 0 to its width and to its height — the edges of the
    // picture carry none, so there is one scale to read and it starts where the outline does.
    const { u: pu, v: pv } = slot.patch;
    const corner = px(pu.lo, pv.lo);
    const middle = px((pu.lo + pu.hi) / 2, (pv.lo + pv.hi) / 2);
    const outY = corner.y >= middle.y ? 1 : -1;
    const outX = corner.x >= middle.x ? 1 : -1;
    const su = stepFor(pu.hi - pu.lo);
    for (let t = 0; t <= pu.hi - pu.lo + 0.01; t += su) {
      const { x, y } = px(pu.lo + t, pv.lo);
      tick(x, y, x, y + outY * 10);
      say(String(t), x, y + outY * (outY > 0 ? 30 : 16), 'center');
    }
    const sv = stepFor(pv.hi - pv.lo);
    for (let t = 0; t <= pv.hi - pv.lo + 0.01; t += sv) {
      const { x, y } = px(pu.lo, pv.lo + t);
      tick(x, y, x + outX * 10, y);
      say(String(t), x + outX * 14, y + 7, outX > 0 ? 'left' : 'right');
    }
    ctx.font = '500 17px ui-sans-serif, system-ui, sans-serif';
    // NAMED BY ITS AXIS, not by an edge: the product's left and right are the picture's right and
    // left on a back face, and "the outline's right edge" drawn on the left read wrong.
    say(slot.axes.u, (corner.x + px(pu.hi, pv.lo).x) / 2, corner.y + outY * (outY > 0 ? 54 : 40), 'center');
    ctx.save();
    const sideX = corner.x + outX * 62;
    ctx.translate(sideX, (corner.y + px(pu.lo, pv.hi).y) / 2);
    ctx.rotate(-Math.PI / 2);
    say(slot.axes.v, 0, 0, 'center');
    ctx.restore();
    return out.toDataURL('image/png');
  }

  // NO OUTLINE: the whole face, with its rulers along the bottom and the left of the picture, in mm
  // from the part's own edges.
  const su = stepFor(f.ru.hi - f.ru.lo);
  for (let t = Math.ceil(f.ru.lo / su) * su; t <= f.ru.hi; t += su) {
    const { x } = px(t, (f.rv.lo + f.rv.hi) / 2);
    if (x < STRIP || x > STRIP + PX) continue;
    tick(x, PX, x, PX + 10);
    say(String(t), x, PX + 32, 'center');
  }
  const sv = stepFor(f.rv.hi - f.rv.lo);
  for (let t = Math.ceil(f.rv.lo / sv) * sv; t <= f.rv.hi; t += sv) {
    const { y } = px((f.ru.lo + f.ru.hi) / 2, t);
    if (y < 0 || y > PX) continue;
    tick(STRIP - 10, y, STRIP, y);
    say(String(t), STRIP - 13, y + 7, 'right');
  }
  ctx.font = '500 17px ui-sans-serif, system-ui, sans-serif';
  say(`mm from the ${fromU}`, STRIP + PX / 2, PX + 60, 'center');
  ctx.save();
  ctx.translate(16, PX / 2);
  ctx.rotate(-Math.PI / 2);
  say(`mm from the ${fromV}`, 0, 0, 'center');
  ctx.restore();
  return out.toDataURL('image/png');
}

// THE PICTURES OF EACH GROUP OF HOLES, in the order the prompt names them — for the call, and for
// the badge that shows what the call is sent (one function, so the two cannot differ). See
// jointShots. A group that will not render is left out: `shown` is the list actually drawn, and
// the prompt is built from it.
export async function picturedViews(
  ctx: HolesContext,
  meshOf: MeshOf | undefined,
  // As the parts stood before their first bore — the badge's (see withSlotAreas).
  undrilled = false,
  // The holes the answer made, crossed where they were drilled (see jointShots).
  drilled: StepHole[] = [],
): Promise<{ shown: string[]; views: { url: string; caption: string; group: string }[] }> {
  if (!meshOf) return { shown: [], views: [] };
  return jointShots(ctx.slots, ctx.items, meshOf, productMiddleX(ctx.items, meshOf), undrilled, drilled);
}
